import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { saFetch, SALoading, inputCls, btnPrimary, btnOutline } from "./saCommon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CircleNotch, PlugsConnected, GearSix, CreditCard } from "@phosphor-icons/react";

export default function SAConfig() {
  const [settings, setSettings] = useState(null);
  const [paypal, setPaypal] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingPaypal, setSavingPaypal] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(() => {
    saFetch("/superadmin/settings").then(setSettings).catch((e) => toast.error(e.message));
    saFetch("/superadmin/paypal/config").then(setPaypal).catch((e) => toast.error(e.message));
  }, []);
  useEffect(load, [load]);

  if (!settings || !paypal) return <SALoading label="Cargando configuración..." />;

  const setS = (k, v) => setSettings({ ...settings, [k]: v });
  const setP = (k, v) => setPaypal({ ...paypal, [k]: v });

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = { ...settings, tax_rate: Number(settings.tax_rate) || 0, grace_days: Number(settings.grace_days) || 0, trial_days: Number(settings.trial_days) || 0, hnl_usd_rate: Number(settings.hnl_usd_rate) || 0 };
      await saFetch("/superadmin/settings", { method: "PUT", body: JSON.stringify(payload) });
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingSettings(false);
    }
  };

  const savePaypal = async () => {
    setSavingPaypal(true);
    try {
      await saFetch("/superadmin/paypal/config", {
        method: "PUT",
        body: JSON.stringify({ mode: paypal.mode, client_id: paypal.client_id || "", secret: paypal.secret || "", receiver_email: paypal.receiver_email || "", enabled: !!paypal.enabled }),
      });
      toast.success("Configuración PayPal guardada");
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingPaypal(false);
    }
  };

  const testPaypal = async () => {
    setTesting(true);
    try {
      const r = await saFetch("/superadmin/paypal/test", { method: "POST" });
      toast.success(r.detail);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setTesting(false);
    }
  };

  const fieldCls = "text-xs uppercase tracking-widest text-muted-foreground";

  return (
    <div data-testid="sa-config">
      <div className="mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-foreground">Configuración del sistema</h1>
        <p className="text-sm text-muted-foreground mt-1">Parámetros globales de la plataforma y pasarela de pago</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="border border-border bg-card rounded-sm p-5 space-y-4" data-testid="sa-config-general">
          <h3 className="font-heading text-base font-bold text-foreground flex items-center gap-2"><GearSix size={16} className="text-primary" /> General</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className={fieldCls}>Nombre de la plataforma</Label>
              <Input className={inputCls} data-testid="sa-cfg-appname" value={settings.app_name || ""} onChange={(e) => setS("app_name", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className={fieldCls}>Moneda</Label>
              <Select value={settings.currency || "HNL"} onValueChange={(v) => setS("currency", v)}>
                <SelectTrigger className={inputCls} data-testid="sa-cfg-currency"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  <SelectItem value="HNL">Lempiras (HNL)</SelectItem>
                  <SelectItem value="USD">Dólares (USD)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className={fieldCls}>Tasa de impuesto (0.15 = 15%)</Label>
              <Input type="number" step="0.01" min="0" className={inputCls} data-testid="sa-cfg-tax" value={settings.tax_rate ?? 0} onChange={(e) => setS("tax_rate", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className={fieldCls}>Tipo de cambio HNL → USD (PayPal)</Label>
              <Input type="number" step="0.01" min="0" className={inputCls} data-testid="sa-cfg-rate" value={settings.hnl_usd_rate ?? ""} onChange={(e) => setS("hnl_usd_rate", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className={fieldCls}>Días de gracia tras vencimiento</Label>
              <Input type="number" min="0" className={inputCls} data-testid="sa-cfg-grace" value={settings.grace_days ?? 0} onChange={(e) => setS("grace_days", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className={fieldCls}>Días de prueba por defecto</Label>
              <Input type="number" min="0" className={inputCls} data-testid="sa-cfg-trial" value={settings.trial_days ?? 0} onChange={(e) => setS("trial_days", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className={fieldCls}>Correo de contacto</Label>
              <Input type="email" className={inputCls} data-testid="sa-cfg-email" value={settings.contact_email || ""} onChange={(e) => setS("contact_email", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className={fieldCls}>Teléfono de contacto</Label>
              <Input className={inputCls} data-testid="sa-cfg-phone" value={settings.contact_phone || ""} onChange={(e) => setS("contact_phone", e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className={fieldCls}>Pie de página en comprobantes</Label>
            <Input className={inputCls} value={settings.receipt_footer || ""} onChange={(e) => setS("receipt_footer", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className={fieldCls}>Términos y condiciones</Label>
            <Textarea className={`${inputCls} min-h-[70px]`} data-testid="sa-cfg-terms" value={settings.terms_text || ""} onChange={(e) => setS("terms_text", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className={fieldCls}>Política de privacidad</Label>
            <Textarea className={`${inputCls} min-h-[70px]`} data-testid="sa-cfg-privacy" value={settings.privacy_text || ""} onChange={(e) => setS("privacy_text", e.target.value)} />
          </div>
          <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
              <Switch checked={!!settings.maintenance_mode} onCheckedChange={(v) => setS("maintenance_mode", v)} data-testid="sa-cfg-maintenance" />
              Modo mantenimiento (muestra aviso a las empresas)
            </label>
            {settings.maintenance_mode && (
              <Input className={inputCls} placeholder="Mensaje de mantenimiento" data-testid="sa-cfg-maintenance-msg" value={settings.maintenance_message || ""} onChange={(e) => setS("maintenance_message", e.target.value)} />
            )}
          </div>
          <Button onClick={saveSettings} disabled={savingSettings} className={btnPrimary} data-testid="sa-cfg-save">
            {savingSettings && <CircleNotch className="animate-spin mr-2" size={15} />} Guardar configuración
          </Button>
        </div>

        <div className="border border-border bg-card rounded-sm p-5 space-y-4 self-start" data-testid="sa-config-paypal">
          <h3 className="font-heading text-base font-bold text-foreground flex items-center gap-2"><CreditCard size={16} className="text-primary" /> PayPal</h3>
          <p className="text-xs text-muted-foreground">
            Obtén tus credenciales en <span className="text-primary">developer.paypal.com</span> → Apps &amp; Credentials. Empieza en modo Sandbox y pasa a Live cuando estés listo.
          </p>
          <div className="space-y-1.5">
            <Label className={fieldCls}>Modo</Label>
            <Select value={paypal.mode || "sandbox"} onValueChange={(v) => setP("mode", v)}>
              <SelectTrigger className={inputCls} data-testid="sa-paypal-mode"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                <SelectItem value="sandbox">Sandbox (pruebas)</SelectItem>
                <SelectItem value="live">Live (producción)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className={fieldCls}>Client ID</Label>
            <Input className={`${inputCls} font-mono text-xs`} data-testid="sa-paypal-client" value={paypal.client_id || ""} onChange={(e) => setP("client_id", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className={fieldCls}>Secret {paypal.secret_masked ? `(guardado: ${paypal.secret_masked})` : ""}</Label>
            <Input type="password" className={`${inputCls} font-mono text-xs`} data-testid="sa-paypal-secret" placeholder={paypal.secret_masked ? "Dejar vacío para conservar el actual" : "Pega el secret aquí"} value={paypal.secret || ""} onChange={(e) => setP("secret", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className={fieldCls}>Correo receptor (opcional)</Label>
            <Input type="email" className={inputCls} value={paypal.receiver_email || ""} onChange={(e) => setP("receiver_email", e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
            <Switch checked={!!paypal.enabled} onCheckedChange={(v) => setP("enabled", v)} data-testid="sa-paypal-enabled" />
            Habilitar pagos con PayPal
          </label>
          <div className="flex gap-2">
            <Button onClick={savePaypal} disabled={savingPaypal} className={btnPrimary} data-testid="sa-paypal-save">
              {savingPaypal && <CircleNotch className="animate-spin mr-2" size={15} />} Guardar PayPal
            </Button>
            <Button variant="outline" onClick={testPaypal} disabled={testing} className={btnOutline} data-testid="sa-paypal-test">
              {testing ? <CircleNotch className="animate-spin mr-2" size={15} /> : <PlugsConnected className="mr-2" size={15} />} Probar conexión
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Webhook (opcional para confirmación extra): configura en tu app de PayPal la URL
            <span className="font-mono text-foreground/70"> {`${process.env.REACT_APP_BACKEND_URL}/api/paypal/webhook`}</span> con los eventos PAYMENT.CAPTURE.COMPLETED y CHECKOUT.ORDER.APPROVED.
          </p>
        </div>
      </div>
    </div>
  );
}
